import { useCallback, useEffect, useRef, useState } from "react";

import {
  completeStep,
  JourneyApiError,
  openJourney,
  startJourney,
  viewStep,
  type Journey,
} from "./api";
import { brand, steps as stepCopy, ui } from "./content/en";
import { AskZhaturaPanel } from "./journey/AskZhatura";
import {
  interpolate,
  roleName,
  segmentName,
  territoryName,
} from "./journey/format";
import { Perspective } from "./journey/Perspective";
import { StepView } from "./journey/StepView";

import { DashboardRole, session, UserProfile } from "./auth/session";
import { LoginForm } from "./auth/LoginForm";
import { DashboardHeader } from "./components/DashboardHeader";
import { StudentDashboard } from "./dashboards/StudentDashboard";
import { ParentDashboard } from "./dashboards/ParentDashboard";
import { CoachDashboard } from "./dashboards/CoachDashboard";
import { AcademyDashboard } from "./dashboards/AcademyDashboard";

type Phase = "loading" | "ready" | "blocked";

/**
 * The invitation credential travels in the URL fragment.
 *
 * A fragment is never sent to the server in a request line or Referer header and
 * never reaches web-server logs, so the private link survives a reload - which is
 * what makes "leave and come back later" work - without the credential being
 * written to browser storage or observable by any intermediary.
 */
function readCredential(): string {
  return decodeURIComponent(window.location.hash.replace(/^#/, "")).trim();
}

/** jsdom and some embedded browsers do not implement scrolling; never let that throw. */
function scrollToTop(): void {
  try {
    window.scrollTo({ top: 0, behavior: "auto" });
  } catch {
    // Scrolling is a nicety, not journey state.
  }
}

function blockedMessage(failure: unknown): string {
  if (!(failure instanceof JourneyApiError)) return ui.errorGeneric;
  if (failure.code === "NETWORK_UNAVAILABLE") return ui.errorOffline;
  if (failure.status === 429) return ui.errorRateLimited;
  if (failure.code === "INVITATION_NOT_FOUND") return ui.errorNotFound;
  if (failure.code === "INVITATION_UNAVAILABLE") {
    if (failure.invitationStatus === "EXPIRED") return ui.errorExpired;
    if (failure.invitationStatus === "REVOKED") return ui.errorRevoked;
  }
  return ui.errorGeneric;
}

export function App(): React.JSX.Element {
  const [viewMode, setViewMode] = useState<"journey" | "portal">(() => {
    try {
      const hash = window.location.hash.replace(/^#/, "").trim();
      const path = window.location.pathname;
      if (
        path.startsWith("/portal") ||
        path.startsWith("/dashboard") ||
        hash.startsWith("portal") ||
        hash.startsWith("dashboard")
      ) {
        return "portal";
      }
    } catch {
      // Ignore
    }
    return "journey";
  });

  if (viewMode === "portal") {
    return <UnifiedPortalApp onBackToJourney={() => setViewMode("journey")} />;
  }

  return <CandidateJourneyApp onOpenPortal={() => setViewMode("portal")} />;
}

export function UnifiedPortalApp({
  onBackToJourney,
}: {
  onBackToJourney?: () => void;
}): React.JSX.Element {
  const [user, setUser] = useState<UserProfile | null>(() => session.getUser());

  useEffect(() => {
    return session.subscribe((u) => setUser(u));
  }, []);

  if (!user) {
    return <LoginForm onSuccess={() => setUser(session.getUser())} />;
  }

  return (
    <div
      className="unified-portal-shell"
      role="region"
      aria-label="Zhatura Unified Web Portal"
    >
      <DashboardHeader
        user={user}
        activeRole={user.activeRole}
        onRoleChange={(role: DashboardRole) => session.switchRole(role)}
        onLogout={() => {
          session.logout();
          onBackToJourney?.();
        }}
      />
      <main id="portal-main-content">
        {user.activeRole === "student" && <StudentDashboard user={user} />}
        {user.activeRole === "parent" && <ParentDashboard user={user} />}
        {user.activeRole === "coach" && <CoachDashboard user={user} />}
        {user.activeRole === "academy" && <AcademyDashboard user={user} />}
      </main>
    </div>
  );
}

function CandidateJourneyApp({
  onOpenPortal,
}: {
  onOpenPortal?: () => void;
}): React.JSX.Element {
  const [phase, setPhase] = useState<Phase>("loading");
  const [journey, setJourney] = useState<Journey>();
  const [position, setPosition] = useState(1);
  const [message, setMessage] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [credential] = useState(readCredential);
  const viewed = useRef<string>(undefined);
  const headingAnchor = useRef<HTMLDivElement>(null);

  const load = useCallback(async (): Promise<void> => {
    if (credential.length === 0) {
      setMessage(ui.errorMissing);
      setPhase("blocked");
      return;
    }
    setPhase("loading");
    try {
      const value = await openJourney(credential);
      setJourney(value);
      setPosition(value.resume_position);
      setPhase("ready");
    } catch (failure) {
      setMessage(blockedMessage(failure));
      setPhase("blocked");
    }
  }, [credential]);

  useEffect(() => {
    // Deferred by a task so the first paint is never blocked and the load's own
    // state transitions happen outside the effect body.
    const handle = window.setTimeout(() => {
      void load();
    }, 0);
    return () => {
      window.clearTimeout(handle);
    };
  }, [load]);

  const step = journey?.program.steps.find(
    (candidate) => candidate.position === position,
  );
  const started =
    journey !== undefined &&
    (journey.status === "IN_PROGRESS" || journey.status === "COMPLETED");

  // Record that the candidate reached a step. Failure here is never allowed to
  // interrupt the journey: it is operational evidence, not journey state.
  useEffect(() => {
    if (!started || step === undefined) return;
    const marker = `${String(position)}:${step.key}`;
    if (viewed.current === marker) return;
    viewed.current = marker;
    void viewStep(credential, step.key).catch(() => undefined);
  }, [credential, started, step, position]);

  useEffect(() => {
    headingAnchor.current?.focus();
  }, [position]);

  async function advance(): Promise<void> {
    if (journey === undefined || step === undefined || busy) return;
    setBusy(true);
    try {
      let current = journey;
      if (current.status === "ISSUED" || current.status === "OPENED") {
        current = await startJourney(credential);
      }
      current = await completeStep(credential, step.key);
      setJourney(current);
      setPosition((value) =>
        Math.min(value + 1, current.program.final_position),
      );
      scrollToTop();
    } catch (failure) {
      setMessage(blockedMessage(failure));
      setPhase("blocked");
    } finally {
      setBusy(false);
    }
  }

  function goBack(): void {
    setPosition((value) => Math.max(1, value - 1));
    scrollToTop();
  }

  if (phase === "loading") {
    return (
      <main className="notice" aria-busy="true">
        <p>{ui.loading}</p>
      </main>
    );
  }

  if (phase === "blocked" || journey === undefined || step === undefined) {
    return (
      <main className="notice">
        <p className="wordmark">{brand.wordmark}</p>
        <h1>{ui.errorTitle}</h1>
        <p>{message ?? ui.errorGeneric}</p>
        <button
          type="button"
          className="btn primary"
          onClick={() => {
            void load();
          }}
        >
          {ui.retry}
        </button>
        {onOpenPortal && (
          <button
            type="button"
            className="btn quiet"
            style={{ marginTop: "1rem" }}
            onClick={onOpenPortal}
          >
            Access Zhatura Portal (Students, Parents, Coaches, Academies)
          </button>
        )}
      </main>
    );
  }

  const territory = territoryName(journey.territory_code);
  const vars: Readonly<Record<string, string>> = {
    FirstName: journey.candidate_given_name,
    Territory: territory,
    Role: roleName(journey.network_role, journey.founding),
    Segment: segmentName(journey.segment_key) ?? "",
    Founding: journey.founding ? "true" : "false",
  };
  const copy = stepCopy[step.key];
  const isFinal = step.position === journey.program.final_position;
  const completed = journey.status === "COMPLETED";
  const showAssistant = step.assistant_available && journey.assistant_enabled;

  return (
    <div className="shell">
      <a className="skip-link" href="#journey-step">
        {brand.skipToContent}
      </a>
      <header className="masthead">
        <span className="wordmark">{brand.wordmark}</span>
        {step.shows_progress && (
          <p className="progress">
            <span className="progress-track" aria-hidden="true">
              <span
                className="progress-fill"
                style={{
                  width: `${String(
                    (step.position / journey.program.final_position) * 100,
                  )}%`,
                }}
              />
            </span>
            <span>
              {interpolate(ui.progress, {
                current: String(step.position),
                total: String(journey.program.final_position),
              })}
            </span>
          </p>
        )}
      </header>

      <main className="stage" id="journey-step">
        <article className="step">
          <div ref={headingAnchor} tabIndex={-1} />
          {step.kind === "PERSPECTIVE" ? (
            <Perspective
              journey={journey}
              credential={credential}
              vars={vars}
              onSaved={setJourney}
            />
          ) : (
            <StepView journey={journey} step={step} vars={vars} />
          )}
        </article>
      </main>

      {showAssistant && !asking && (
        <button
          type="button"
          className="ask-launcher"
          onClick={() => {
            setAsking(true);
          }}
        >
          {ui.askZhatura} ✦
        </button>
      )}
      {showAssistant && asking && (
        <AskZhaturaPanel
          credential={credential}
          stepKey={step.key}
          onClose={() => {
            setAsking(false);
          }}
        />
      )}

      <div className="footer-bar">
        <div className="footer-inner">
          {step.position > 1 && (
            <button type="button" className="btn quiet" onClick={goBack}>
              {ui.back}
            </button>
          )}
          {!(isFinal && completed) && (
            <button
              type="button"
              className="btn primary"
              disabled={busy}
              onClick={() => {
                void advance();
              }}
            >
              {interpolate(copy?.cta ?? "", vars)}
              {isFinal ? " ✓" : " →"}
            </button>
          )}
          {isFinal && completed && (
            <button
              type="button"
              className="btn quiet"
              onClick={() => {
                setPosition(1);
                scrollToTop();
              }}
            >
              {ui.reviewJourney}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
